import { ActionIcon, Badge, Button, Card, Group, Progress, Stack, Text, Title } from "@mantine/core";
import { formatFileSize } from "@packages/lib";
import { IconTrash, IconUpload } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useRef } from "react";
import { type FileUploadEntry, useFileUpload } from "~/hooks/use-file-upload";

export const Route = createFileRoute("/file-upload")({
  component: RouteComponent,
});

function RouteComponent() {
  const { add, remove, uploads } = useFileUpload({ folder: "uploads", type: "private" });
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter(
      (f) => !uploads.some((u) => u.file.name === f.name && u.file.size === f.size),
    );
    if (files.length > 0) add(files);
    // Reset so selecting the same file again triggers onChange
    e.target.value = "";
  };

  return (
    <Stack p="xl" maw={560} mx="auto" mt="xl">
      <Title order={3}>File Upload</Title>
      <Card withBorder radius="md" p="lg">
        <Stack gap="md">
          <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={handleChange} />
          <Button leftSection={<IconUpload size={16} />} onClick={() => inputRef.current?.click()}>
            Select files
          </Button>

          {uploads.length > 0 && (
            <Stack gap="xs">
              {uploads.map((entry, i) => (
                <FileUploadDisplay key={entry.fileId ?? i} entry={entry} remove={remove} />
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

function FileUploadDisplay({ entry, remove }: { entry: FileUploadEntry; remove: (fileId: string) => void }) {
  return (
    <Card withBorder radius="sm" p="xs">
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" align="center">
            <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
              {entry.file.name}
            </Text>
            <Badge
              size="xs"
              variant="light"
              color={
                entry.status === "done"
                  ? "green"
                  : entry.status === "error"
                    ? "red"
                    : entry.status === "uploading"
                      ? "blue"
                      : "gray"
              }>
              {entry.status === "uploading" ? `${entry.progress}%` : entry.status}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {formatFileSize(entry.file.size)}
          </Text>
          {entry.status !== "idle" && (
            <Progress
              value={entry.progress}
              animated={entry.status === "uploading"}
              color={entry.status === "error" ? "red" : entry.status === "done" ? "green" : "blue"}
              size="xs"
              mt={4}
            />
          )}
          {entry.status === "error" && (
            <Text size="xs" c="red">
              {entry.error?.message ?? "Upload failed"}
            </Text>
          )}
        </Stack>
        <ActionIcon
          variant="subtle"
          color="red"
          disabled={!entry.fileId}
          onClick={() => entry.fileId && remove(entry.fileId)}>
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Card>
  );
}
